import { useMemo } from 'react'
import { Alert, Card, Flex, Space, Tag, Tooltip, Typography } from 'antd'
import { WarningOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { EmptyState } from '@/shared/ui/EmptyState'
import { isPartner } from './identityApi'
import type { PanelUser, PermissionInfo, Role } from './identityApi'
import { permissionLabel } from './permissionLabel'

interface UserPermissionsPanelProps {
  user: PanelUser
  roles: readonly Role[]
  catalog: readonly PermissionInfo[]
  /** Rollar ro'yxati hali kelmagan — huquqlarni hisoblab bo'lmaydi */
  loading: boolean
  /** Operatorda `role.read` yo'q: rollar ro'yxati umuman olinmaydi */
  rolesUnavailable: boolean
}

interface ComputedPermission {
  code: string
  dangerous: boolean
  /** Qaysi rol(lar) shu huquqni beryapti — "nega bu odam buni qila oladi" savoliga javob */
  grantedBy: readonly string[]
}

/**
 * Foydalanuvchining HISOBLANGAN huquqlari.
 *
 * ⚠️ NEGA HISOBLANADI, SO'RALMAYDI: `xsib-identity` da
 * `GET /api/users/{id}/permissions` endpointi YO'Q (`UserResource.java` —
 * to'liq o'qildi). Huquqlar rol orqali beriladi va rolning huquqlari
 * `GET /api/roles` javobida keladi, ya'ni birlashma shu yerda aniq
 * hisoblanadi. Backend `AccessGuard` ham AYNAN shu birlashmani ishlatadi
 * (`UserRoleRepository.permissionsOf` → `role_permission`), demak natija
 * bir xil manbadan.
 *
 * ⚠️ Faqat rol NOMINI ko'rsatish yetarli emas: "ADMIN" yoki "SUPPORT" degan
 * yorliq operatorga bu odam pul tuzata oladimi yoki yo'qmi degan savolga
 * javob bermaydi. Shuning uchun har bir huquq alohida chiziladi, xavflilari
 * esa ajratib ko'rsatiladi (`Permission.DANGEROUS`).
 */
export function UserPermissionsPanel({
  user,
  roles,
  catalog,
  loading,
  rolesUnavailable,
}: UserPermissionsPanelProps) {
  const { t } = useTranslation()

  const computed = useMemo<readonly ComputedPermission[]>(() => {
    const dangerous = new Set(catalog.filter((p) => p.dangerous).map((p) => p.code))
    const byCode = new Map<string, Set<string>>()

    for (const assignment of user.roles) {
      const role = roles.find((r) => r.id === assignment.roleId)
      if (!role) continue
      // Panel bilmaydigan kodlar ham ko'rsatiladi: ular serverda HAQIQATAN
      // amal qiladi, yashirish esa huquqlar ro'yxatini yolg'on qilardi
      for (const code of [...role.permissions, ...role.unknownPermissions]) {
        const holders = byCode.get(code) ?? new Set<string>()
        holders.add(role.code)
        byCode.set(code, holders)
      }
    }

    return [...byCode.entries()]
      .map(([code, holders]) => ({
        code,
        dangerous: dangerous.has(code),
        grantedBy: [...holders].sort(),
      }))
      .sort((a, b) => a.code.localeCompare(b.code))
  }, [user.roles, roles, catalog])

  const groups = useMemo(() => {
    const map = new Map<string, ComputedPermission[]>()
    for (const permission of computed) {
      const prefix = permission.code.split('.')[0] ?? permission.code
      const list = map.get(prefix) ?? []
      list.push(permission)
      map.set(prefix, list)
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  }, [computed])

  const dangerousCount = computed.filter((p) => p.dangerous).length

  return (
    <Card
      title={t('identity.permissions.title')}
      loading={loading}
      extra={
        computed.length > 0 ? (
          <Space size={4}>
            <Tag>{t('identity.permissions.count', { count: computed.length })}</Tag>
            {dangerousCount > 0 ? (
              <Tag color="red" icon={<WarningOutlined />}>
                {t('identity.permissions.dangerousCount', { count: dangerousCount })}
              </Tag>
            ) : null}
          </Space>
        ) : null
      }
    >
      {/*
        ⚠️ SHERIK — hisoblanadigan narsa YO'Q va bo'lmaydi ham.

        Uning huquqlari `identity.role` da emas, serverda KODDA
        (`PartnerKind`, ADR-029) va `GET /api/roles` ularni umuman
        qaytarmaydi. Ya'ni yuqoridagi birlashma sherik uchun HAR DOIM
        bo'sh chiqadi va odatdagi bo'sh holat («Rol berilmagan, shuning
        uchun hech qanday huquq yo'q») YOLG'ON bo'lardi: sherikda
        huquqlar BOR, ular shunchaki boshqa manbadan keladi.

        To'plamni bu yerda qo'lda takrorlash ham RAD ETILDI: u ikkinchi
        haqiqat manbai bo'lardi va kodda bir qator o'zgarganda panel
        jimgina eskirardi.
      */}
      {isPartner(user) ? (
        <Alert
          type="info"
          showIcon
          message={t('identity.partner.staticSetTitle')}
          description={t('identity.partner.permissionsText')}
        />
      ) : rolesUnavailable ? (
        <Alert type="info" showIcon message={t('identity.permissions.needRoleRead')} />
      ) : computed.length === 0 ? (
        <EmptyState
          // Ikonka o'rniga variant: bo'sh holat illyustratsiyasi endi
          // `EmptyState` ichida, butun panel bo'yicha bitta joyda
          variant="no-permission"
          title={t('identity.permissions.emptyTitle')}
          description={
            user.roles.length === 0
              ? t('identity.permissions.emptyNoRoles')
              : t('identity.permissions.emptyNoPerms')
          }
        />
      ) : (
        <>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
            {t('identity.permissions.hint')}
          </Typography.Paragraph>
          <Flex vertical gap={12}>
            {groups.map(([prefix, list]) => (
              <div key={prefix}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {/*
                    ⚠️ Nuqson #4 — MANBA BITTA: `permissionGroup.*`.

                    Ilgari bu yerda `identity.permGroup.*` turardi va u
                    rol konstruktoridagi (`PermissionPicker`)
                    `permissionGroup.*` dan MUSTAQIL lug'at edi. Ikkita
                    lug'at ikkita muammo bergan:

                      1. Tarjimalar ajralib ketgan — bitta huquq toifasi
                         ikki ekranda ikki nom bilan: "Tarmoq qurilmalari"
                         ↔ "Tarmoq uskunasi", "To'lov tizimlari" ↔
                         "To'lov provayderi", "Hisob" ↔ "Buxgalteriya".
                      2. Ikkinchi lug'at TO'LIQ EMAS edi: `site`, `tenant`
                         va `attendance` unda umuman yo'q. `defaultValue`
                         tufayli ular jimgina XOM KOD bo'lib chizilardi —
                         testerlar ko'rgan "site" va "tenant" aynan shu.

                    `permissionGroup.*` da `PERMISSIONS` dagi 21 domenning
                    hammasi bor, shuning uchun manba sifatida o'sha
                    tanlandi. Bu ikkita tarjimani sinxron tutish emas,
                    ikkinchisini UMUMAN YO'Q QILISH — sinxron tutish
                    qoidasi doim qachondir buziladi.
                  */}
                  {t(`permissionGroup.${prefix}`, { defaultValue: prefix })}
                </Typography.Text>
                <div style={{ marginTop: 4 }}>
                  <Space size={[4, 6]} wrap>
                    {list.map((permission) => (
                      <Tooltip
                        key={permission.code}
                        // ⚠️ BUG-042 — XOM KOD tooltipda QOLADI: yorliq endi
                        // odam tilida, lekin qo'llab-quvvatlash xizmati
                        // aynan kod bilan gapiradi (`permissionLabel`
                        // javadoc'i). Teg ichida ikkalasi ham sig'masdi —
                        // shuning uchun kod tooltipga tushdi.
                        title={`${permission.code} — ${t('identity.permissions.grantedBy', {
                          roles: permission.grantedBy.join(', '),
                        })}`}
                      >
                        <Tag
                          color={permission.dangerous ? 'red' : 'default'}
                          icon={permission.dangerous ? <WarningOutlined /> : undefined}
                          style={{ marginInlineEnd: 0 }}
                        >
                          {permissionLabel(permission.code, t)}
                        </Tag>
                      </Tooltip>
                    ))}
                  </Space>
                </div>
              </div>
            ))}
          </Flex>
        </>
      )}
    </Card>
  )
}
